"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider } from "next-auth/react";
import { ThemeProvider } from "next-themes";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RequestIdBadge } from "@/components/debug/RequestIdBadge";
import { StaleActionReloader } from "@/components/stale-action-reloader";

/**
 * Client-side providers wrapper for the application.
 *
 * Wraps children with:
 * - ThemeProvider: Dark/light mode via next-themes (class-based)
 * - SessionProvider: Auth.js session context
 * - QueryClientProvider: TanStack Query for client-side data fetching
 * - TooltipProvider: Required by shadcn/ui tooltip components
 * - Toaster (Sonner): Toast notifications
 *
 * This is a client component because QueryClientProvider requires
 * React context. The root layout.tsx (a Server Component) wraps
 * its children with <Providers>.
 */
export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000, // 1 minute default -- topics don't change frequently
            retry: 1,
          },
        },
      })
  );

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <SessionProvider>
        <NuqsAdapter>
          <TooltipProvider>
            <QueryClientProvider client={queryClient}>
              {children}
              <Toaster />
              <RequestIdBadge />
              {/* Catches Next.js "Failed to find Server Action" errors that
                  fire when a user's browser bundle predates the most recent
                  deploy, and soft-reloads the page so the new bundle loads. */}
              <StaleActionReloader />
            </QueryClientProvider>
          </TooltipProvider>
        </NuqsAdapter>
      </SessionProvider>
    </ThemeProvider>
  );
}
